import { WifiOffIcon } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { useRealtimeStatus } from '@/shared/realtime';
import { Spinner } from '@/shared/ui/spinner';

function subscribeOnline(onChange: () => void) {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

const useOnline = () =>
  useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );

/** Grace period before showing "reconnecting" to avoid flicker on short network blips. */
const RECONNECT_GRACE_MS = 2500;

/** Thin banner when the browser is offline or the realtime connection is being re-established. */
export function ConnectionBanner() {
  const { t } = useTranslation('app');
  const online = useOnline();
  const status = useRealtimeStatus();
  const [graceOver, setGraceOver] = useState(false);
  const reconnecting = status === 'reconnecting';

  useEffect(() => {
    if (!reconnecting) return;
    const id = setTimeout(() => setGraceOver(true), RECONNECT_GRACE_MS);
    return () => {
      clearTimeout(id);
      setGraceOver(false);
    };
  }, [reconnecting]);

  if (online && !(reconnecting && graceOver)) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto mb-3 flex w-fit animate-in items-center gap-2 rounded-full border border-warning/30 bg-warning-soft px-3.5 py-1.5 text-[0.8125rem] font-medium text-foreground shadow-xs fade-in slide-in-from-top-1"
    >
      {online ? (
        <Spinner className="size-3.5 text-warning-text" />
      ) : (
        <WifiOffIcon className="size-3.5 text-warning-text" />
      )}
      {online ? t('connection.reconnecting') : t('connection.offline')}
    </div>
  );
}
